// API base URL from frontend/.env (REACT_APP_API_BASE_URL)
export const getApiBaseUrl = () => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  if (!baseUrl) {
    console.warn('REACT_APP_API_BASE_URL is not set in .env');
    return 'http://localhost:4001/api';
  }
  return baseUrl.replace(/\/$/, '');
};

// Enhanced fetch function with retry capability
const fetchWithRetry = async (url, options = {}, retries = 1) => {
  try {
    const response = await fetch(url, options);
    return response;
  } catch (error) {
    if (retries > 0 && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
      console.log(`Connection failed. Retrying... (${retries} retries left)`);
      return fetchWithRetry(url, options, retries - 1);
    }
    throw error;
  }
};

// Function to fetch debug table info
export const fetchTableDebugInfo = async () => {
  const baseUrl = getApiBaseUrl();
  
  try {
    const response = await fetchWithRetry(`${baseUrl}/debug/table-info`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch table debug info');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching table debug info:', error);
    throw error;
  }
};

// Function to fetch user info by UserPK
export const fetchUserInfo = async (userPK) => {
  const baseUrl = getApiBaseUrl();
  
  try {
    const response = await fetchWithRetry(`${baseUrl}/user-info/${userPK}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch user info');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching user info:', error);
    throw error;
  }
};

// Function to fetch user vehicle data by UserPK
export const fetchUserVehicles = async (userPK) => {
  const baseUrl = getApiBaseUrl();
  
  try {
    const response = await fetchWithRetry(`${baseUrl}/user-vehicles/${userPK}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch user vehicles');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching user vehicles:', error);
    throw error;
  }
};

// Function to fetch user data by employee ID
export const fetchUserByEmployeeId = async (employeeId) => {
  const baseUrl = getApiBaseUrl();
  
  try {
    const response = await fetchWithRetry(`${baseUrl}/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ employeeId }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch user data');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw error;
  }
};

// Function to fetch simplified user info by UserPK
export const fetchSimpleUserInfo = async (userPK, tryPhoto = true) => {
  const baseUrl = getApiBaseUrl();
  
  try {
    // Add query param to try fetching the photo if requested
    const queryParam = tryPhoto ? '?tryPhoto=true' : '';
    const response = await fetchWithRetry(`${baseUrl}/user-simple/${userPK}${queryParam}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch user info');
    }
    
    const data = await response.json();
    
    // Log diagnostics if available
    if (data.diagnostics) {
      console.log('User photo diagnostics:', data.diagnostics);
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching simple user info:', error);
    throw error;
  }
};

// Function to fetch unique violations and calculate scores
export const fetchUniqueViolationsAndScores = async (userPK, vehicleID, locationID) => {
  const baseUrl = getApiBaseUrl();
  
  try {
    const response = await fetchWithRetry(`${baseUrl}/get-unique-violations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userPK, vehicleID, locationID }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch violations and scores');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching violations and scores:', error);
    throw error;
  }
};

// Function to refresh the violations and scores cache
export const refreshViolationsCache = async (userPK, vehicleID, locationID) => {
  const baseUrl = getApiBaseUrl();
  
  try {
    const response = await fetchWithRetry(`${baseUrl}/refresh-violations-cache`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userPK, vehicleID, locationID }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to refresh violations cache');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error refreshing violations cache:', error);
    throw error;
  }
};

// Function to fetch maximum session number for a user and vehicle type
export const fetchSessionOptions = async (userPK, vehicleName) => {
  const baseUrl = getApiBaseUrl();
  
  try {
    // Using URL parameters to safely pass the values for the SQL query placeholders
    const response = await fetchWithRetry(`${baseUrl}/session-options?userPK=${encodeURIComponent(userPK)}&vehicleName=${encodeURIComponent(vehicleName)}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch session options');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching session options:', error);
    throw error;
  }
};

// Function to fetch vehicle assessment data by UserPK and VehicleName
export const fetchVehicleAssessmentData = async (userPK, vehicleName, sessionNumber = null) => {
  const baseUrl = getApiBaseUrl();
  
  try {
    // Using URL parameters to safely pass the values for the SQL query placeholders
    let url = `${baseUrl}/vehicle-assessment-data?userPK=${encodeURIComponent(userPK)}&vehicleName=${encodeURIComponent(vehicleName)}`;
    
    // Add sessionNumber parameter if provided
    if (sessionNumber !== null) {
      url += `&sessionNumber=${encodeURIComponent(sessionNumber)}`;
    }
    
    const response = await fetchWithRetry(url);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch vehicle assessment data');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching vehicle assessment data:', error);
    throw error;
  }
};

// Function to authenticate user with EmployeeID and Password
export const loginUser = async (employeeID, password) => {
  const baseUrl = getApiBaseUrl();
  
  try {
    const response = await fetchWithRetry(`${baseUrl}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ employeeID, password }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Invalid credentials');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error during login:', error);
    throw error;
  }
};

// Function to fetch detailed violation data for a specific attempt
export const fetchViolationDetails = async (userPK, assessmentNumber, attemptNumber, vehicleID) => {
  const baseUrl = getApiBaseUrl();
  
  try {
    // Using URL parameters to safely pass the values for the SQL query placeholders
    const url = `${baseUrl}/violation-details?userPK=${encodeURIComponent(userPK)}&assessmentNumber=${encodeURIComponent(assessmentNumber)}&attemptNumber=${encodeURIComponent(attemptNumber)}&vehicleID=${encodeURIComponent(vehicleID)}`;
    
    const response = await fetchWithRetry(url);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch violation details');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching violation details:', error);
    throw error;
  }
};

// Function to fetch all vehicle remarks
export const fetchVehicleRemarks = async () => {
  const baseUrl = getApiBaseUrl();
  
  try {
    const response = await fetchWithRetry(`${baseUrl}/vehicle-remarks`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch vehicle remarks');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching vehicle remarks:', error);
    throw error;
  }
};

// Function to check Remarks content in user_vehicle table
export const checkRemarks = async () => {
  const baseUrl = getApiBaseUrl();
  
  try {
    const response = await fetchWithRetry(`${baseUrl}/check-remarks`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to check remarks data');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking remarks data:', error);
    throw error;
  }
};

// Function to fetch vehicle leaderboard data
export const fetchVehicleLeaderboard = async (vehicleID, userPK) => {
  const baseUrl = getApiBaseUrl();
  
  try {
    console.log('fetchVehicleLeaderboard called with:', { vehicleID, userPK });
    
    const url = `${baseUrl}/vehicle-leaderboard?vehicleID=${encodeURIComponent(vehicleID)}&userPK=${encodeURIComponent(userPK)}`;
    
    console.log('Sending request to:', url);
      
    const response = await fetchWithRetry(url);
    const data = await response.json();
    
    if (!response.ok) {
      console.error('Leaderboard API error:', data);
      
      // Try fallback to test endpoint if main fails
      console.log('Trying fallback test endpoint...');
      const fallbackUrl = `${baseUrl}/test-leaderboard?vehicleID=${encodeURIComponent(vehicleID)}&userPK=${encodeURIComponent(userPK)}`;
      const fallbackResponse = await fetchWithRetry(fallbackUrl);
      
      if (!fallbackResponse.ok) {
        throw new Error(`Failed to fetch vehicle leaderboard: ${data.error || response.statusText}`);
      }
      
      const fallbackData = await fallbackResponse.json();
      console.log('Using fallback test data:', fallbackData);
      return fallbackData;
    }
    
    console.log('Leaderboard API response:', data);
    
    // Make sure we have what we need
    if (!data.leaderboard || !Array.isArray(data.leaderboard)) {
      console.error('Invalid leaderboard data format:', data);
      throw new Error('Invalid leaderboard data format');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching vehicle leaderboard:', error);
    
    // Return mock data as absolute fallback
    const mockData = {
      leaderboard: [
        { Rankings: 1, EmployeeName: 'Top Player', UserPK: 100 },
        { Rankings: 2, EmployeeName: 'Runner Up', UserPK: 101 },
        { Rankings: 3, EmployeeName: 'Third Place', UserPK: 102 }
      ],
      userRank: 3,
      source: 'error-fallback'
    };
    
    console.log('Returning mock data due to error');
    return mockData;
  }
};

// Function to fetch user assessment details final data
export const fetchUserAssessmentDetails = async (userPK) => {
  const baseUrl = getApiBaseUrl();
  
  try {
    const url = `${baseUrl}/user-assessment-details-final?userPK=${encodeURIComponent(userPK)}`;
    console.log('Fetching user assessment details from:', url);
    
    const response = await fetchWithRetry(url);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch user assessment details');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching user assessment details:', error);
    throw error;
  }
};

// Function to register a new user
export const signupUser = async (userData) => {
  const baseUrl = getApiBaseUrl();
  
  try {
    const response = await fetchWithRetry(`${baseUrl}/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Registration failed');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error during signup:', error);
    throw error;
  }
};

// Function to fetch working areas for signup
export const fetchWorkingAreas = async (locationID = 2) => {
  const baseUrl = getApiBaseUrl();
  
  try {
    const response = await fetchWithRetry(`${baseUrl}/working-areas?locationID=${locationID}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch working areas');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching working areas:', error);
    throw error;
  }
};

// Function to run the update script for user_vehicle remarks
export const runUpdateScript = async () => {
  const baseUrl = getApiBaseUrl();

  try {
    const response = await fetchWithRetry(`${baseUrl}/run-update-script`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    return await response.json();
  } catch (error) {
    console.error('Error running update script:', error);
    throw error;
  }
};